import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import avatarUrl from '@/assets/images/logo/kolesa-avatar.png';
import axios from '@/axios';
import { checkRole, userTypes, STATUS_NO_TOKEN } from '@/constants';
import { buildUser } from '@/utils/user';
import type { SessionUser, User } from '@/types/user';

declare global {
    interface Window {
        xdm?:    { sess?: { token?: string } };
        jQuery?: object;
    }
}

const getDefaultUser = (): User => ({
    isAuthorized: false,
    name:         '',
    userRole:     '',
});

export const useUserStore = defineStore('user', () => {
    const user = ref<User>(getDefaultUser());

    const isAuthorized = computed(() => user.value.isAuthorized);
    const isPro = computed(() => checkRole(userTypes[4], user.value.userRole));

    /**
     * Сохраняет данные пользователя из ответа бэка
     */
    function setUser(data: SessionUser) {
        user.value = {
            ...buildUser(user.value, data),
            photoURL: avatarUrl,
        };
    }

    /**
     * Сбрасывает данные пользователя
     */
    function resetUser() {
        user.value = getDefaultUser();
    }

    /**
     * Авторизоваться на сервере по ключу
     *
     * @return успешна ли авторизация
     */
    async function loginByToken(token: string): Promise<boolean> {
        try {
            const { data: { data } } = await axios.get('login', { params: { token } });
            setUser(data);

            return true;
        } catch {
            return false;
        }
    }

    /**
     * Проверка xdm авторизации: грузим скрипт AUTH и логинимся по его токену
     *
     * @return успешна ли авторизация
     */
    function checkXdm(assetUrl: string): Promise<boolean> {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = assetUrl;
            script.onload = async () => {
                const token = window.xdm?.sess?.token;

                resolve(token ? await loginByToken(token) : false);
            };
            // Скрипт не загрузился — считаем, что не авторизованы, иначе гард роутера зависнет
            script.onerror = () => resolve(false);

            // хак для работы js файла от AUTH
            window.jQuery = {};
            document.getElementsByTagName('head')[0].appendChild(script);
        });
    }

    /**
     * Получение данных по сессии
     *
     * @return авторизован ли пользователь
     */
    async function getSession(): Promise<boolean> {
        try {
            const { data: { data } } = await axios.get('session');
            setUser(data);

            return true;
        } catch (error) {
            const response = (error as { response?: { status?: number; data?: { assetUrl?: string } } })
                ?.response;
            const assetUrl = response?.data?.assetUrl;

            if (response?.status === STATUS_NO_TOKEN && assetUrl) return checkXdm(assetUrl);

            return false;
        }
    }

    /**
     * Разлогинивает на сервере и уходит на страницу из ответа
     */
    async function logout() {
        const { data: { destination } } = await axios.get('logout');
        resetUser();

        window.location = destination;
    }

    return {
        getSession,
        isAuthorized,
        isPro,
        logout,
        resetUser,
        user,
    };
});
